// Bezug der Vorauswahl (Pillen-Formular UebergabeVorauswahl.tsx) — verbindet die vorab
// gewählte Art/Objekt/Einheit/Kontakte mit dem neu angelegten Protokoll: legeKopfzeileAn
// schreibt sofort eine Kopfzeile mit Status "in_erfassung" (das Büro sieht dadurch, dass
// eine Übergabe läuft, noch bevor am Objekt irgendetwas erfasst wurde), verteileRollen
// füllt daraus die zwei Vertragsparteien des Assistenten (Schritt 1, dort weiterhin
// änderbar).
//
// Rollenlogik (verbindliche Vorgabe, s. Task-1-Brief): einzug → Mieter wird
// Übernehmer; auszug → Mieter wird Übergeber; kauf → Eigentümer wird Übergeber, der
// Käufer wird Übernehmer. Ein Zeuge wird NICHT mehr automatisch angelegt (Entscheidung
// des Auftraggebers, s. Task-2-Brief) — er ist optional und wird bei Bedarf im
// Assistenten hinzugefügt (fuegeZeugenHinzu, lib/uebergabe/unterschriften.ts). Der
// Eigentümer wird bei einzug/auszug bewusst NICHT als Partei geführt (Entscheidung des
// Auftraggebers) — deshalb bleibt kontakte.eigentuemer in diesen beiden Fällen
// unangetastet, selbst wenn er mitgegeben wurde.
//
// Fix-Runde 1 (Review): beim Kauf kommen BEIDE Seiten aus den Eigentümern — der Mieter
// ist dort komplett aus dem Spiel (auch datenschutzlogisch: der aktuelle Mieter der
// Einheit hat mit einem Eigentümerwechsel nichts zu tun und darf nicht versehentlich
// als "Käufer" auftauchen). Der Käufer ist ein ZWEITER Eigentümer-Kontakt
// (`kontakte.kaeufer`/`Bezug.kaeufer_eigentuemer_id`), der — anders als der bisherige
// Eigentümer — keine eigene Spalte in `uebergabeprotokolle` hat (s. Bezug unten).
import { supabase } from '../supabase/client';
import { eigentuemerAnzeigename } from '../eigentuemer/anzeige';
import { neuePartei } from './unterschriften';
import type { Art, Partei } from './typen';

export interface Bezug {
  art: Art;
  objekt_id: string | null;
  einheit_id: string | null;
  mieter_id: string | null;
  eigentuemer_id: string | null;
  /**
   * Fix-Runde 1 (Review): nur bei `kauf` relevant — der Käufer, sofern bereits als
   * Eigentümer angelegt (der "zweite gewählte Kontakt" der Rollenlogik). Hat KEINE
   * eigene Spalte in `uebergabeprotokolle` (die Tabelle kennt nur EINE eigentuemer_id,
   * die beim Kauf weiterhin den BISHERIGEN Eigentümer/Übergeber trägt — dieselbe
   * Semantik wie `objekte.eigentuemer_id`) und wird deshalb von legeKopfzeileAn NICHT
   * persistiert. Er fließt nur in die Parteien (verteileRollen/ladeKontakteFuerBezug);
   * bleibt er leer, bleibt der Übernehmer-Name leer und wird in Schritt 1 des
   * Assistenten von Hand eingetragen.
   */
  kaeufer_eigentuemer_id?: string | null;
}

/** Kontakt aus der `mieter`-Tabelle — Vor-/Nachname getrennt, wie in der Tabelle selbst. */
export interface MieterKontakt {
  vorname?: string | null;
  nachname?: string | null;
  firma_name?: string | null;
  email?: string | null;
  telefon?: string | null;
  anschrift?: string | null;
}

/** Kontakt aus der `eigentuemer`-Tabelle — Name bereits aufgelöst (Firma vs. Person, s. lib/eigentuemer/anzeige.ts). */
export interface EigentuemerKontakt {
  name: string;
  email?: string | null;
  telefon?: string | null;
  anschrift?: string | null;
}

export interface Kontakte {
  mieter?: MieterKontakt;
  eigentuemer?: EigentuemerKontakt;
  /** Nur bei `kauf` genutzt — der Käufer (zweiter Eigentümer-Kontakt), s. Bezug.kaeufer_eigentuemer_id. */
  kaeufer?: EigentuemerKontakt;
}

export interface MitarbeiterInfo {
  name?: string | null;
  email?: string | null;
}

// neuePartei() (statt eines lokalen Literals) — sonst führe die Vertragsparteien hier
// ohne `pid` in die Welt, und gehoertZu()/parteiLabel() bräuchten wieder den Rollen-
// Rückfall, den es für sie eigentlich gar nicht mehr braucht.
function ausMieter(rolle: Partei['rolle'], m: MieterKontakt | undefined): Partei {
  if (!m) return neuePartei(rolle);
  const name = (m.firma_name?.trim()) || `${m.vorname ?? ''} ${m.nachname ?? ''}`.replace(/\s+/g, ' ').trim();
  return neuePartei(rolle, { name, anschrift: m.anschrift ?? '', email: m.email ?? '', telefon: m.telefon ?? '' });
}

function ausEigentuemer(rolle: Partei['rolle'], e: EigentuemerKontakt | undefined): Partei {
  if (!e) return neuePartei(rolle);
  return neuePartei(rolle, { name: e.name ?? '', anschrift: e.anschrift ?? '', email: e.email ?? '', telefon: e.telefon ?? '' });
}

/**
 * Reine Funktion (keine Netzwerkzugriffe): verteilt die vorab gewählten Kontakte auf
 * die zwei Vertragsparteirollen, je nach Art der Übergabe. Bezug wird nur auf `art`
 * gelesen (Pick statt vollem Bezug), damit der Aufruf auch VOR dem Anlegen der Kennung
 * möglich ist (s. UebergabeVorauswahl.tsx, das noch keine protokollId kennt).
 *
 * `mitarbeiter` bleibt Teil der Signatur, obwohl kein automatischer Zeuge mehr daraus
 * entsteht (Task-2-Brief) — beide Aufrufstellen (UebergabeListe.tsx, UebergabeAssistent.tsx)
 * geben ihn ohnehin mit, und ihn hier wegzunehmen wäre eine Signaturänderung, die dieser
 * Task nicht verlangt.
 */
export function verteileRollen(
  bezug: Pick<Bezug, 'art'>,
  kontakte: Kontakte,
  mitarbeiter: MitarbeiterInfo,
): Partei[] {
  void mitarbeiter;
  let uebergeber: Partei;
  let uebernehmer: Partei;
  switch (bezug.art) {
    case 'einzug':
      uebergeber = neuePartei('uebergeber');
      uebernehmer = ausMieter('uebernehmer', kontakte.mieter);
      break;
    case 'auszug':
      uebergeber = ausMieter('uebergeber', kontakte.mieter);
      uebernehmer = neuePartei('uebernehmer');
      break;
    case 'kauf':
      uebergeber = ausEigentuemer('uebergeber', kontakte.eigentuemer);
      uebernehmer = ausEigentuemer('uebernehmer', kontakte.kaeufer);
      break;
  }
  return [uebergeber, uebernehmer];
}

/** Lädt einen einzelnen Eigentümer-Kontakt per ID — gemeinsam genutzt für den (bisherigen) Eigentümer UND den Käufer (Kauf). */
async function ladeEigentuemerKontakt(id: string): Promise<EigentuemerKontakt | undefined> {
  const { data, error } = await supabase
    .from('eigentuemer').select('anrede, vorname, nachname, firma_name, email, telefon, strasse, hausnummer, plz, stadt')
    .eq('id', id).maybeSingle();
  if (error || !data) return undefined;
  const strasse = `${data.strasse ?? ''} ${data.hausnummer ?? ''}`.trim();
  const ort = `${data.plz ?? ''} ${data.stadt ?? ''}`.trim();
  return {
    name: eigentuemerAnzeigename(data),
    email: data.email,
    telefon: data.telefon,
    anschrift: [strasse, ort].filter(Boolean).join(', '),
  };
}

/**
 * Lädt die Anzeigedaten (Name/E-Mail/Telefon/Anschrift) zu den in einem Bezug
 * gesetzten Kontakt-IDs — Trennung von `verteileRollen` (reine Funktion, s. Test)
 * bewusst so gehalten, dass die eigentliche Zuordnungslogik ohne Netzwerk testbar
 * bleibt. Fehler beim Laden werden NICHT geworfen — eine nicht ladbare Kontaktzeile
 * darf die Neuanlage des Assistenten nicht verhindern (Aufrufer erhält einfach eine
 * leere Kontakte-Struktur und verteileRollen füllt entsprechend leer).
 */
export async function ladeKontakteFuerBezug(bezug: Bezug): Promise<Kontakte> {
  const kontakte: Kontakte = {};

  if (bezug.mieter_id) {
    const { data, error } = await supabase
      .from('mieter').select('vorname, nachname, firma_name, email, telefon')
      .eq('id', bezug.mieter_id).maybeSingle();
    if (!error && data) {
      kontakte.mieter = {
        vorname: data.vorname, nachname: data.nachname, firma_name: data.firma_name,
        email: data.email, telefon: data.telefon,
      };
    }
  }

  if (bezug.eigentuemer_id) {
    const eigentuemer = await ladeEigentuemerKontakt(bezug.eigentuemer_id);
    if (eigentuemer) kontakte.eigentuemer = eigentuemer;
  }

  if (bezug.kaeufer_eigentuemer_id) {
    const kaeufer = await ladeEigentuemerKontakt(bezug.kaeufer_eigentuemer_id);
    if (kaeufer) kontakte.kaeufer = kaeufer;
  }

  return kontakte;
}

// Diese Felder verwaltet der Client beim Anlegen der Kopfzeile — der Rest der Tabelle
// (parteien, raeume, zaehler, schluessel, unterschriften, pdf/mail-Felder) bekommt
// seine Datenbank-Defaults ('[]'::jsonb bzw. null), der eigentliche Assistent füllt sie
// später über denselben Upsert-Pfad wie beim Abschluss (lib/uebergabe/abschluss.ts).
/**
 * Legt sofort eine Kopfzeile in `uebergabeprotokolle` mit Status "in_erfassung" an,
 * damit das Büro sieht, dass eine Übergabe läuft, noch bevor am Objekt irgendetwas
 * erfasst wurde. Fehler werden geworfen, NICHT verschluckt — der Aufrufer entscheidet,
 * ob ein Fehlschlag (z. B. kein Netz) die Erfassung blockiert oder nicht (laut Design:
 * nicht — s. UebergabeListe.tsx, das den Assistenten trotzdem startet und nur einen
 * Hinweis zeigt).
 */
export async function legeKopfzeileAn(bezug: Bezug, protokollId: string, erfasstVon: string | null): Promise<void> {
  const { error } = await supabase.from('uebergabeprotokolle').upsert({
    id: protokollId,
    art: bezug.art,
    status: 'in_erfassung',
    objekt_id: bezug.objekt_id,
    einheit_id: bezug.einheit_id,
    mieter_id: bezug.mieter_id,
    eigentuemer_id: bezug.eigentuemer_id,
    // bezug.kaeufer_eigentuemer_id bewusst NICHT hier — keine Spalte dafür (s. Bezug-
    // Interface oben), er fließt nur in die Parteien-JSON (verteileRollen).
    erfasst_von: erfasstVon,
    durchgefuehrt_am: new Date().toISOString(),
  });
  if (error) throw error;
}
